import { format, parse, parseISO } from 'date-fns';
import { cs, de, enUS, es, fr, nl, pt } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';

import type { QuizByIdResponse } from '@/services/httpServices/QuizServices/types';
import type { FormOptions } from '@/types/general';
import { getTargetEnvironmentClient } from '@/utils/target-detection';

// Mapping of locale codes to date-fns Locale objects
const dateLocales: Record<string, typeof enUS> = {
  de,
  cs,
  es,
  nl,
  pt,
  fr,
  en: enUS,
};

export function getBaseUrl() {
  return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3333';
}

export function getFullDomain() {
  const urlObj = new URL(getBaseUrl());
  return urlObj.hostname;
}

export function convertToLocalDate(isoTimestamp: string): string {
  const date = parseISO(isoTimestamp);
  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const localDate = toZonedTime(date, timeZone);
  return format(localDate, 'dd/MM/yyyy HH:mm:ss');
}

export function openInNewTab(url: string) {
  const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (newWindow) {
    newWindow.opener = null;
  }
}

export function redirectToUrl(url: string) {
  window.location.href = url;
}

/**
 * Formats a group ID in the format YYYY-MM to a readable month and year (Month YYYY)
 * @param groupId - The group ID to format (YYYY-MM)
 * @param locale - Optional locale code (e.g., 'de', 'fr') for localized month names
 * @returns Formatted date string (Month YYYY) or the original string if invalid
 */
export function formatGroupDate(groupId: string, locale?: string): string {
  if (groupId === null || groupId === undefined || groupId === '') {
    return '';
  }

  const [year, month] = groupId.split('-');
  if (year === undefined || year === '' || month === undefined || month === '') {
    return groupId;
  }

  try {
    // Parse the date using date-fns
    const parsedDate = parse(`${year}-${month}-01`, 'yyyy-MM-dd', new Date());
    // Get the date-fns locale object if provided
    const dateFnsLocale = locale !== undefined && locale !== '' ? dateLocales[locale] : undefined;
    // Format the date using date-fns with optional locale
    return format(parsedDate, 'MMMM yyyy', { locale: dateFnsLocale });
  } catch (error) {
    console.warn('Error formatting group date:', error);
    return groupId;
  }
}

/**
 * Converts a TypeScript enum to FormOptions with label and value properties.
 * @param enumObj - The enum to convert.
 * @returns FormOptions - An array of FormOption objects with label and value.
 */
export function enumToFormOptions(
  enumObj: Record<string, string>,
): FormOptions {
  return Object.values(enumObj).map(value => ({
    label: value ? value.replace(/([A-Z])/g, ' $1').trim() : '', // Add spaces to camelCase values
    value,
  }));
}

export function formatDateTime(input: Date | string | undefined, locale?: string): string {
  if (input === undefined || input === null || input === '') {
    return '';
  }

  const dateObj = new Date(input);

  if (dateObj.toString() === 'Invalid Date') {
    return '';
  }

  return dateObj
    .toLocaleString(locale || undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(',', '');
}

export type DurationType = {
  value: number;
  unit: 'seconds' | 'minutes';
};

export function formatDuration(durationInMs: number | null | undefined): { formatted: string; type: DurationType | null } {
  if (durationInMs === undefined || durationInMs === null) {
    return { formatted: '-', type: null };
  }

  const seconds = Math.round(durationInMs / 1000);
  if (seconds < 60) {
    return {
      formatted: `${seconds}s`,
      type: { value: seconds, unit: 'seconds' },
    };
  }

  const minutes = Math.round(seconds / 60);
  return {
    formatted: `${minutes}m`,
    type: { value: minutes, unit: 'minutes' },
  };
}

/**
 * Calculate word count from quiz questions and answers
 */
export function calculateWordCount(data?: QuizByIdResponse): number {
  if (data?.data?.questions === undefined) {
    return 0;
  }

  return data?.data?.questions.reduce((total, q) => {
    const questionWordCount = q.question ? q.question.split(' ').length : 0;
    const answersWordCount = q.answers
      ? q.answers.reduce((ansTotal, ans) => {
          return ansTotal + (ans.answer ? ans.answer.split(' ').length : 0);
        }, 0)
      : 0;

    return total + questionWordCount + answersWordCount;
  }, 0);
}

/**
 * Constructs a URL for the legacy CMS with the target parameter if it exists
 * @param basePath - The hash path to append after the legacy CMS URL (should start with #)
 * @param targetEnv - The target environment to use (optional, will be detected from cookies if not provided)
 * @returns Full URL with target parameter if targetEnv exists
 */
export function getLegacyCMSUrlWithTarget(basePath: string, targetEnv?: string): string {
  const legacyCmsUrl = process.env.NEXT_PUBLIC_LEGACY_CMS_APP_URL || '';

  // Get target from parameter or from cookies (client-side only)
  let target = targetEnv;
  if ((target === undefined || target === '') && typeof window !== 'undefined') {
    target = getTargetEnvironmentClient();
  }

  if (target === undefined || target === null || target === '') {
    return `${legacyCmsUrl}${basePath}`;
  }

  return `${legacyCmsUrl}?target=${target}${basePath}`;
}

/**
 * Constructs a URL for the Next Editor in POC CMS with complete routing structure
 * This generates the final URL that directly goes to the coaches/space/next-editor with iframe URL
 *
 * @param iframeUrl - The dashboard URL to load in the iframe (must be a valid URL)
 * @param space - The active space identifier
 * @returns Complete POC CMS URL for Next Editor access, or empty string if construction fails
 *
 * @example
 * ```typescript
 * const editorUrl = getNextEditorUrl('https://dashboard.example.com/course/123', 'my.space');
 * // Returns: 'https://legacy-cms.com?target=samwise#/coaches/my.space/next-editor?iframeUrl=...'
 * ```
 */
export function getNextEditorUrl(iframeUrl: string, space: string): string {
  // Validate input
  if (iframeUrl === null || iframeUrl === undefined || typeof iframeUrl !== 'string' || iframeUrl.trim().length === 0) {
    return '';
  }

  if (space === null || space === undefined || typeof space !== 'string' || space.trim().length === 0) {
    return '';
  }

  const legacyCmsUrl = process.env.NEXT_PUBLIC_LEGACY_CMS_APP_URL;
  if (legacyCmsUrl === null || legacyCmsUrl === undefined || legacyCmsUrl === '') {
    return '';
  }

  try {
    // Clean and normalize the input URL
    const cleanedIframeUrl = iframeUrl.trim().replace(/\s+/g, '');

    // Validate the cleaned URL
    try {
      // eslint-disable-next-line no-new
      new URL(cleanedIframeUrl);
    } catch {
      return '';
    }

    // Get target environment from cookies (client-side only)
    let targetEnv = '';
    if (typeof window !== 'undefined') {
      targetEnv = getTargetEnvironmentClient();
    }

    // Use the provided space parameter
    const activeSpace = space.trim();

    // Construct the base URL with target parameter following getLegacyCMSUrlWithTarget pattern
    // Only add target parameter if targetEnv exists and is not empty (excludes production)
    let finalUrl = legacyCmsUrl.trim();
    if (targetEnv !== undefined && targetEnv !== null && targetEnv !== '') {
      finalUrl += `?target=${targetEnv}`;
    }

    // Add the hash fragment following POC CMS pattern: #/coaches/{space}/next-editor?iframeUrl=...
    // Only encode the space name, keep iframe URL unencoded in the fragment
    const encodedActiveSpace = encodeURIComponent(activeSpace);
    finalUrl += `#/coaches/${encodedActiveSpace}/next-editor?iframeUrl=${cleanedIframeUrl}`;

    return finalUrl;
  } catch (error) {
    // Log error in development, return empty string in production
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to construct Next Editor URL:', error);
    }
    return '';
  }
}
